import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PhoneCall } from 'lucide-react'

/**
 * Shown the moment a registration finishes with an unverified number.
 *
 * An unverified member is invisible: nobody can call them and they can call
 * nobody, and nothing on the profile page says so loudly enough — the yellow
 * banner there is easy to scroll past. This asks the one question that
 * matters right now and takes them straight to verification. "Later" is
 * allowed: the banner stays until they do it.
 */
const VerifyNumberModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="verify-number-title"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-card border border-border rounded-xl max-w-sm w-full p-6 shadow-strong text-center">
        <div className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center mx-auto mb-4">
          <PhoneCall className="w-7 h-7 text-primary-foreground" />
        </div>

        <h2 id="verify-number-title" className="text-lg font-bold text-foreground mb-3">
          {t('verifyPrompt.title')}
        </h2>

        <p role="alert"
           className="text-sm leading-relaxed mb-6 rounded-lg border-2 border-yellow-400 bg-yellow-50 text-yellow-900 px-3 py-2.5">
          {t('verifyPrompt.body')}
        </p>

        <button
          autoFocus
          type="button"
          onClick={() => navigate('/verify-otp')}
          className="w-full px-4 py-2.5 bg-gradient-primary text-primary-foreground rounded-lg font-medium
                     hover:opacity-90 transition-opacity shadow-soft
                     focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          {t('verifyPrompt.now')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-full mt-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground
                     focus:outline-none focus:ring-2 focus:ring-border rounded-lg"
        >
          {t('verifyPrompt.later')}
        </button>
      </div>
    </div>,
    document.body
  )
}

export default VerifyNumberModal
